import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useEffect, useMemo, useState } from "react";
import { useInterval } from "usehooks-ts";
import { RedoPlatformPublicRpcClientContext } from "../platform/redo-platform-rpc-client-provider";
import {
  removeSessionVersionNumber,
  sessionNeedsRefreshing,
  setSessionVersionNumber,
} from "./session-expiration";

const SESSION_EXPIRY_CHANNEL = "redo-channel";

export function useSessionExpired(): boolean {
  const platformClient = useRequiredContext(RedoPlatformPublicRpcClientContext);

  const [sessionExpired, setSessionExpired] = useState(false);

  const broadcastChannel = useMemo(() => {
    if (typeof BroadcastChannel !== "undefined") {
      return new BroadcastChannel(SESSION_EXPIRY_CHANNEL);
    }
    return null;
  }, []);

  const handleMessage = useHandler((event: MessageEvent) => {
    if (event.data?.type === "SESSION_EXPIRED") {
      setSessionExpired(true);
    }
  });

  useEffect(() => {
    if (broadcastChannel) {
      broadcastChannel.addEventListener("message", handleMessage);
    }

    return () => {
      if (broadcastChannel) {
        broadcastChannel.removeEventListener("message", handleMessage);
        broadcastChannel.close();
      }
    };
  }, [broadcastChannel, handleMessage]);

  // Initialize the session on load
  useLoad(async () => {
    const { version } = await platformClient.checkSession({});
    if (version != null) {
      setSessionVersionNumber(version);
    }
  }, []);

  useInterval(
    async () => {
      const { version } = await platformClient.checkSession({});

      const shouldRefresh = sessionNeedsRefreshing({ currentVersion: version });

      if (shouldRefresh) {
        if (broadcastChannel) {
          broadcastChannel.postMessage({ type: "SESSION_EXPIRED" });
        }
        setSessionExpired(true);
      }

      if (version != null) {
        setSessionVersionNumber(version);
      } else {
        removeSessionVersionNumber();
      }
    },
    Temporal.Duration.from({ minutes: 5 }).total("milliseconds"),
  );

  return sessionExpired;
}
